import { useEffect, useRef, useState } from 'react'
import { sayInGame, type GameSide, type GameView } from '../chatv1/transport'
import { Icon } from '../ui/Icon'
import { useVoiceRecorder } from '../voice/useVoiceRecorder'
import { transcribeBlob } from '../voice/transcribe'
import { insertAtCaret } from '../voice/insertAtCaret'
import { micGate, recordCapMs, reportVoiceError } from '../billing/voiceGate'
import { gameErrorText, seatName } from './gameText'

/**
 * TALKING TO THE OTHER PLAYER — owner, 2026-10-19: 在對弈的棋局裡面, 使用者可以使用語音輸入信息傳達給他的對弈的對手.
 * 或者是使用者也可以直接手動輸入訊息傳達給他的對手.
 *
 * Typed, or spoken: the microphone is the app's own voice input (the composer's and the notes' — same recorder, same
 * transcription, same daily allowance), and what was said lands in the box as text to check and send. Both players
 * see every line; the other gets a banner when they are not looking.
 */
export function GameTalk({ g, onSent }: { g: GameView; onSent: (g: GameView) => void }) {
  const them: GameSide = g.you === 'w' ? 'b' : 'w'
  return (
    <TalkBox
      to={seatName(g, them)}
      lines={g.chat.map((c, i) => ({ key: `${c.at}-${i}`, mine: c.from === g.you, who: seatName(g, c.from), text: c.text }))}
      send={async (text) => onSent((await sayInGame(g.id, text)).game)}
    />
  )
}

/**
 * The message box itself — any game's: the lines said (yours on the right), and a box to type or speak into. `to`
 * names who reads it ("Y Tai", or "the table" at mahjong).
 */
export function TalkBox({
  to,
  lines,
  send: deliver,
}: {
  to: string
  lines: Array<{ key: string; mine: boolean; who: string; text: string }>
  send: (text: string) => Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const list = useRef<HTMLOListElement>(null)

  // The newest line in view, whenever one arrives.
  useEffect(() => {
    const el = list.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lines.length])

  const voice = useVoiceRecorder({
    onClip: async (blob, durationMs) => {
      try {
        const r = await transcribeBlob(blob, durationMs)
        if (!r.text.trim()) {
          setErr('No speech was detected.')
          return
        }
        const el = box.current
        const next = insertAtCaret(draft, el?.selectionStart ?? draft.length, el?.selectionEnd ?? draft.length, r.text)
        setDraft(next.value)
        setErr(null)
        requestAnimationFrame(() => {
          box.current?.focus()
          box.current?.setSelectionRange(next.caret, next.caret)
        })
      } catch (e) {
        reportVoiceError(e, setErr)
      }
    },
    onProblem: setErr,
    maxMs: recordCapMs,
  })
  const recording = voice.state === 'recording'
  const micBusy = voice.state === 'starting' || voice.state === 'uploading'

  const send = async () => {
    const text = draft.trim()
    if (!text || busy) return
    setBusy(true)
    setErr(null)
    try {
      await deliver(text)
      setDraft('')
    } catch (e) {
      setErr(gameErrorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="gm-talk" aria-label={`Messages with ${to}`}>
      {lines.length > 0 ? (
        <ol ref={list} className="gm-talk-list">
          {lines.map((c) => (
            <li key={c.key} className={c.mine ? 'mine' : 'theirs'}>
              {!c.mine && <span className="gm-talk-who">{c.who}</span>}
              <span className="gm-talk-text">{c.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="gh-note gm-talk-empty">Say something to {to} — type it, or tap the microphone and speak.</p>
      )}
      <div className="gm-talk-row">
        <textarea
          ref={box}
          className="gm-talk-input"
          rows={1}
          maxLength={500}
          value={draft}
          placeholder={recording ? 'Listening…' : `Message ${to}`}
          aria-label={`Message ${to}`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              void send()
            }
          }}
        />
        <button
          type="button"
          className={`gm-talk-mic${recording ? ' on' : ''}`}
          aria-label={recording ? 'Stop and write it down' : 'Speak a message'}
          aria-pressed={recording}
          disabled={micBusy}
          onClick={() => {
            if (micGate(voice.state)) return
            voice.toggle()
          }}
        >
          <Icon name="mic" size={18} />
        </button>
        <button
          type="button"
          className="gm-talk-send"
          aria-label="Send"
          disabled={!draft.trim() || busy}
          onClick={() => void send()}
        >
          <Icon name="send" size={17} />
        </button>
      </div>
      {voice.state === 'uploading' && <p className="gh-note">Writing down what you said…</p>}
      {err && <p className="gh-note mch-err">{err}</p>}
    </section>
  )
}
